import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont, ludsHeadingXS, ludsTypography } from '../../shared/styles/typography';
import { CheckboxEvents, CheckboxGroupEvents, CheckboxOption, CheckedEvent } from '../../shared/types/checkbox.model';
import '../checkbox/checkbox';

@customElement('luds-checkbox-group')
export class CheckboxGroup extends LitElement {
  /**
   * List of checkbox options
   */
  @property({ type: Array }) options: Array<CheckboxOption> = [];

  /**
   * Group label
   */
  @property({ type: String, reflect: true }) label = '';

  /**
   * Use HTML Group label
   */
  @property({ type: Boolean }) useHtmlLabel = false;

  static override styles = [
    overrideDesignTokens,
    ludsTypography,
    ludsHeadingXS,
    ludsBaseFont,
    css`
      :host {
        display: block;
      }

      .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-spacing-12);
      }

      .question-label {
        word-break: break-all;
      }
    `,
  ];

  private handleChecked(event: CustomEvent<CheckedEvent>) {
    const { value, checked } = event.detail;

    // Update checkboxes
    this.options = this.options.map((option) => (option.value === value ? { ...option, checked } : option));
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent(CheckboxGroupEvents.VALUE_CHANGED, {
        detail: { options: this.options },
        bubbles: true,
        composed: true,
      })
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(CheckboxEvents.CHECKED, this.handleChecked as EventListener);
  }

  override disconnectedCallback() {
    this.removeEventListener(CheckboxEvents.CHECKED, this.handleChecked as EventListener);
    super.disconnectedCallback();
  }

  override render() {
    return html`
      <div class="checkbox-group">
        ${this.label
          ? html`<label class="h-xs question-label">${this.useHtmlLabel ? unsafeHTML(this.label) : this.label}</label>`
          : null}
        ${this.options.map(
          (option) => html`
            <luds-checkbox
              .label="${option.label || ''}"
              .useHtmlLabel="${option.useHtmlLabel || false}"
              .value="${option.value}"
              .checked="${option.checked || false}"
              .disabled="${option.disabled || false}"
              .indeterminate="${option.indeterminate || false}"
              .checkboxAriaLabel="${option.checkboxAriaLabel || ''}"
              .name="${option.name || ''}"
              .id="${option.id || ''}">
            </luds-checkbox>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-checkbox-group': CheckboxGroup;
  }

  interface WindowEventMap {
    [CheckboxGroupEvents.VALUE_CHANGED]: CustomEvent<{ options: Array<CheckboxOption> }>;
  }
}
